import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-aria-components/Button';
import { Dialog, DialogTrigger } from 'react-aria-components/Dialog';
import { Modal, ModalOverlay } from 'react-aria-components/Modal';
import { useAnnouncer } from './Announcer';

type Theme = 'dark' | 'light';

function storedNumber(key: string, fallback: number): number {
  try {
    const parsed = Number(localStorage.getItem(key));
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function storedBoolean(key: string): boolean {
  try {
    return localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function store(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // La preferencia se aplica durante la visita aunque no pueda persistirse.
  }
}

export function AccessibilityToolbar() {
  const { announce } = useAnnouncer();
  const dialogRef = useRef<HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [fontScale, setFontScale] = useState(() => storedNumber('ta-font-scale', 1));
  const [reduceMotion, setReduceMotion] = useState(() => storedBoolean('ta-reduce-motion'));
  const [theme, setTheme] = useState<Theme>(() =>
    document.documentElement.dataset.bsTheme === 'dark' ? 'dark' : 'light',
  );

  const openPanel = useCallback(() => setOpen(true), []);

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.key.toLowerCase() !== 'a') return;
      event.preventDefault();
      if (open) dialogRef.current?.focus();
      else openPanel();
    };
    document.addEventListener('keydown', onShortcut);
    return () => document.removeEventListener('keydown', onShortcut);
  }, [open, openPanel]);

  const changeFontScale = (value: number) => {
    const next = Math.round(Math.min(1.5, Math.max(0.85, value)) * 100) / 100;
    setFontScale(next);
    store('ta-font-scale', String(next));
    document.documentElement.style.setProperty('--a11y-font-scale', String(next));
    announce(`Tamaño de texto: ${Math.round(next * 100)} por ciento.`);
  };

  const toggleTheme = () => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    store('ta-theme', next);
    document.documentElement.dataset.bsTheme = next;
    announce(`Tema cambiado a ${next === 'dark' ? 'oscuro' : 'claro'}.`);
  };

  const toggleMotion = () => {
    const next = !reduceMotion;
    setReduceMotion(next);
    store('ta-reduce-motion', String(next));
    document.documentElement.classList.toggle('a11y-reduce-motion', next);
    announce(`Movimiento reducido ${next ? 'activado' : 'desactivado'}.`);
  };

  return (
    <DialogTrigger isOpen={open} onOpenChange={setOpen}>
      <Button
        className="btn btn-outline-light a11y-toolbar-toggle"
        aria-expanded={open}
        aria-controls="a11y-panel"
        aria-keyshortcuts="Alt+A"
      >
        <i className="bi bi-universal-access-circle" aria-hidden="true" />
        <span className="d-none d-xl-inline ms-2">Accesibilidad</span>
        <span className="visually-hidden d-xl-none">Opciones de accesibilidad</span>
      </Button>

      <ModalOverlay className="a11y-modal-overlay">
        <Modal className="a11y-toolbar-modal">
          <Dialog ref={dialogRef} id="a11y-panel" className="a11y-toolbar-panel" aria-labelledby="a11y-panel-title">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 id="a11y-panel-title" className="h5 mb-0">Opciones de accesibilidad</h2>
              <Button slot="close" className="btn-close" aria-label="Cerrar opciones de accesibilidad" />
            </div>

            <div className="a11y-toolbar-row">
              <span id="a11y-font-label">Tamaño de texto</span>
              <div role="group" aria-labelledby="a11y-font-label" className="d-flex gap-2">
                <Button className="a11y-btn" onPress={() => changeFontScale(fontScale - 0.1)} aria-label="Disminuir tamaño de texto">A−</Button>
                <Button className="a11y-btn" onPress={() => changeFontScale(1)} aria-label="Restablecer tamaño de texto">A</Button>
                <Button className="a11y-btn" onPress={() => changeFontScale(fontScale + 0.1)} aria-label="Aumentar tamaño de texto">A+</Button>
              </div>
            </div>

            <div className="a11y-toolbar-row">
              <span id="a11y-theme-label">Tema</span>
              <Button className="a11y-btn" onPress={toggleTheme} aria-pressed={theme === 'dark'} aria-labelledby="a11y-theme-label">
                <i className={`bi ${theme === 'dark' ? 'bi-moon-stars' : 'bi-sun'}`} aria-hidden="true" />
                <span>{theme === 'dark' ? 'Oscuro' : 'Claro'}</span>
              </Button>
            </div>

            <div className="a11y-toolbar-row a11y-toolbar-shortcuts">
              <h3 className="h6 mb-1">Atajos de teclado</h3>
              <dl className="mb-0">
                <div><dt>Alt + A</dt><dd>Estas opciones</dd></div>
                <div><dt>Alt + M</dt><dd>El menú principal</dd></div>
              </dl>
            </div>

            <div className="a11y-toolbar-row">
              <span id="a11y-motion-label">Reducir movimiento</span>
              <Button className="a11y-btn" onPress={toggleMotion} aria-pressed={reduceMotion} aria-labelledby="a11y-motion-label">
                {reduceMotion ? 'Activado' : 'Desactivado'}
              </Button>
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </DialogTrigger>
  );
}
